import React, { useEffect, useState } from "react";
import { Plus, Pencil, Loader2, MapPin, Power, Trash2 } from "lucide-react";
import { authFetch } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminLoading from "../../components/admin/AdminLoading";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import AdminTable, { AdminMobileCard, adminTableHeadClass, adminTableRowClass, adminClickableRowClass } from "../../components/admin/AdminTable";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { toast } from "sonner";
import {
  ADMIN_BADGE_OK,
  ADMIN_BADGE_OFF,
  ADMIN_FIELD,
  ADMIN_LABEL,
  ADMIN_PANEL,
  BTN_OUTLINE,
  BTN_PRIMARY,
} from "../../config/brand";

const emptyForm = {
  name: "",
  address: "",
  postal_code: "",
  city: "",
  phone: "",
  email: "",
  active: true,
};

export default function AdminCenters() {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch("/api/admin/centers");
      if (res.ok) setCenters(await res.json());
      else toast.error("Impossible de charger les centres");
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      address: item.address || "",
      postal_code: item.postal_code || "",
      city: item.city || "",
      phone: item.phone || "",
      email: item.email || "",
      active: item.active !== false,
    });
    setErrors({});
    setDialogOpen(true);
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Le nom est requis";
    if (!form.city.trim()) next.city = "La ville est requise";
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = "E-mail invalide";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (!validate()) return;

    setSaving(true);
    const payload = {
      name: form.name.trim(),
      address: form.address.trim(),
      postal_code: form.postal_code.trim(),
      city: form.city.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      active: form.active !== false,
    };
    try {
      const url = editing ? `/api/admin/centers/${editing.id}` : "/api/admin/centers";
      const method = editing ? "PUT" : "POST";
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur");
      }
      toast.success(editing ? "Centre mis à jour" : "Centre créé");
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    setTogglingId(item.id);
    const nextActive = item.active === false;
    try {
      const res = await authFetch(`/api/admin/centers/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: nextActive }),
      });
      if (!res.ok) throw new Error("Erreur");
      toast.success(nextActive ? "Centre activé" : "Centre désactivé — les réservations existantes sont conservées");
      load();
    } catch {
      toast.error("Impossible de changer le statut");
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await authFetch(`/api/admin/centers/${deleteTarget.id}`, { method: "DELETE" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.detail || "Impossible de supprimer le centre");
      }
      const extra =
        body.schedules_deactivated > 0
          ? ` ${body.schedules_deactivated} créneau${body.schedules_deactivated > 1 ? "x" : ""} désactivé${body.schedules_deactivated > 1 ? "s" : ""}.`
          : "";
      toast.success(`Centre « ${deleteTarget.name} » supprimé.${extra} Les inscriptions existantes sont conservées.`);
      setDeleteTarget(null);
      load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleting(false);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <div>
      <AdminPageHeader
        icon={MapPin}
        title="Centres"
        description="Adresses des locaux. Désactiver retire le centre du site sans le supprimer. Supprimer l’efface définitivement (créneaux désactivés, inscriptions conservées)."
        action={
          <Button onClick={openCreate} className={BTN_PRIMARY}>
            <Plus className="h-4 w-4 mr-2" />
            Ajouter
          </Button>
        }
      />

      {loading ? (
        <AdminLoading message="Chargement des centres..." />
      ) : centers.length === 0 ? (
        <AdminEmptyState
          icon={MapPin}
          title="Aucun centre"
          description="Ajoutez un premier local pour l’afficher sur le site et dans le tunnel de réservation."
          action={
            <Button onClick={openCreate} className={BTN_PRIMARY}>
              <Plus className="h-4 w-4 mr-2" />
              Ajouter
            </Button>
          }
        />
      ) : (
        <AdminTable
          mobileCards={centers.map((item) => (
            <AdminMobileCard
              key={item.id}
              title={item.name}
              subtitle={item.city || undefined}
              onClick={() => openEdit(item)}
              badge={
                <Badge className={item.active !== false ? ADMIN_BADGE_OK : ADMIN_BADGE_OFF}>
                  {item.active !== false ? "Actif" : "Inactif"}
                </Badge>
              }
              meta={[
                { label: "Adresse", value: [item.address, item.postal_code].filter(Boolean).join(", ") },
                { label: "Téléphone", value: item.phone },
                { label: "E-mail", value: item.email },
              ]}
              actions={
                <>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(item)} aria-label="Modifier">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleActive(item)}
                    disabled={togglingId === item.id}
                    className={item.active === false ? "text-emerald-600" : "text-amber-600"}
                  >
                    {item.active === false ? "Activer" : "Désactiver"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => setDeleteTarget(item)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Supprimer
                  </Button>
                </>
              }
            />
          ))}
        >
          <Table>
            <TableHeader>
              <TableRow className={`${adminTableRowClass} hover:bg-transparent`}>
                <TableHead className={adminTableHeadClass}>Nom</TableHead>
                <TableHead className={adminTableHeadClass}>Adresse</TableHead>
                <TableHead className={adminTableHeadClass}>Ville</TableHead>
                <TableHead className={adminTableHeadClass}>Téléphone</TableHead>
                <TableHead className={adminTableHeadClass}>E-mail</TableHead>
                <TableHead className={adminTableHeadClass}>Statut</TableHead>
                <TableHead className={`${adminTableHeadClass} text-right`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {centers.map((item) => (
                <TableRow key={item.id} className={adminClickableRowClass} onClick={() => openEdit(item)}>
                  <TableCell className="text-slate-900 font-medium">{item.name}</TableCell>
                  <TableCell className="text-slate-400">
                    {[item.address, item.postal_code].filter(Boolean).join(", ") || "—"}
                  </TableCell>
                  <TableCell className={ADMIN_LABEL}>{item.city || "—"}</TableCell>
                  <TableCell className="text-slate-400">{item.phone || "—"}</TableCell>
                  <TableCell className="text-slate-400">{item.email || "—"}</TableCell>
                  <TableCell>
                    <Badge className={item.active !== false ? ADMIN_BADGE_OK : ADMIN_BADGE_OFF}>
                      {item.active !== false ? "Actif" : "Inactif"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(item)} aria-label="Modifier">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(item)}
                      disabled={togglingId === item.id}
                      aria-label={item.active === false ? "Activer" : "Désactiver"}
                      className={item.active === false ? "text-emerald-600" : "text-amber-600"}
                    >
                      {togglingId === item.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Power className="h-4 w-4 mr-1" />
                          {item.active === false ? "Activer" : "Désactiver"}
                        </>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => setDeleteTarget(item)}
                      aria-label={`Supprimer ${item.name}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Supprimer
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AdminTable>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={`${ADMIN_PANEL} text-slate-900 max-w-lg`}>
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier le centre" : "Nouveau centre"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="center-name" className={ADMIN_LABEL}>Nom *</Label>
              <Input
                id="center-name"
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="Nice Gambetta"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? "center-name-error" : undefined}
                className={`${ADMIN_FIELD} ${errors.name ? "border-red-500" : ""}`}
              />
              {errors.name && (
                <p id="center-name-error" className="text-red-600 text-xs mt-1" role="alert">
                  {errors.name}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="center-address" className={ADMIN_LABEL}>Adresse</Label>
              <Input
                id="center-address"
                value={form.address}
                onChange={(e) => updateField("address", e.target.value)}
                placeholder="15 Boulevard Gambetta"
                className={ADMIN_FIELD}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="center-postal" className={ADMIN_LABEL}>Code postal</Label>
                <Input
                  id="center-postal"
                  value={form.postal_code}
                  onChange={(e) => updateField("postal_code", e.target.value)}
                  placeholder="06000"
                  className={ADMIN_FIELD}
                />
              </div>
              <div>
                <Label htmlFor="center-city" className={ADMIN_LABEL}>Ville *</Label>
                <Input
                  id="center-city"
                  value={form.city}
                  onChange={(e) => updateField("city", e.target.value)}
                  placeholder="Nice"
                  aria-invalid={!!errors.city}
                  aria-describedby={errors.city ? "center-city-error" : undefined}
                  className={`${ADMIN_FIELD} ${errors.city ? "border-red-500" : ""}`}
                />
                {errors.city && (
                  <p id="center-city-error" className="text-red-600 text-xs mt-1" role="alert">
                    {errors.city}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="center-phone" className={ADMIN_LABEL}>Téléphone</Label>
                <Input
                  id="center-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                  placeholder="06 98 85 49 48"
                  className={ADMIN_FIELD}
                />
              </div>
              <div>
                <Label htmlFor="center-email" className={ADMIN_LABEL}>E-mail</Label>
                <Input
                  id="center-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  placeholder="nice@digirobot.fr"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "center-email-error" : undefined}
                  className={`${ADMIN_FIELD} ${errors.email ? "border-red-500" : ""}`}
                />
                {errors.email && (
                  <p id="center-email-error" className="text-red-600 text-xs mt-1" role="alert">
                    {errors.email}
                  </p>
                )}
              </div>
            </div>
            <label htmlFor="center-active" className={`flex items-center gap-2 text-sm ${ADMIN_LABEL} cursor-pointer`}>
              <input
                id="center-active"
                type="checkbox"
                checked={form.active !== false}
                onChange={(e) => updateField("active", e.target.checked)}
                className="rounded border-slate-300"
              />
              Actif sur le site et dans le tunnel de réservation
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className={BTN_OUTLINE} disabled={saving}>
              Annuler
            </Button>
            <Button onClick={save} disabled={saving} className={BTN_PRIMARY}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}
        title={`Supprimer « ${deleteTarget?.name || ""} » ?`}
        description={`Le centre « ${deleteTarget?.name || ""} » sera définitivement retiré de la liste. Les créneaux liés seront désactivés. Les inscriptions déjà enregistrées sont conservées.`}
        confirmLabel="Supprimer"
        onConfirm={confirmDelete}
        confirming={deleting}
      />
    </div>
  );
}
